import { useCallback, useEffect, useState } from "react";
import webApi from "@/lib/webApi";

// Convert base64url (VAPID public key) to Uint8Array for browser subscribe API.
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Manages Web Push subscription lifecycle for the IARisk PWA.
 *
 * Returns:
 *   supported   — boolean: browser has Notification + ServiceWorker + PushManager
 *   permission  — "default" | "granted" | "denied"
 *   subscribed  — boolean: there is an active push subscription stored
 *   enabling    — boolean: in-flight while subscribing
 *   enable()    — prompt + subscribe + persist to backend
 *   disable()   — unsubscribe + tell backend to forget
 *   sendTest()  — fire a server-driven test notification at the user
 */
export function usePushNotifications() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [permission, setPermission] = useState(
    supported ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [enabling, setEnabling] = useState(false);

  // Check existing subscription on mount — with a hard timeout so that
  // in-app browsers where `serviceWorker.ready` never resolves don't hang
  // the React render forever.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const readyP = navigator.serviceWorker.ready;
        const timeoutP = new Promise((_, rej) =>
          setTimeout(() => rej(new Error("SW timeout")), 4000),
        );
        const reg = await Promise.race([readyP, timeoutP]);
        if (cancelled) return;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (subErr) {
        // SW may not be ready or webview blocks Push entirely — log so we can
        // see it during testing, but never block the React render.
        console.debug("[usePushNotifications] subscription probe skipped:", subErr?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) return { ok: false, error: "Browser no soporta push" };
    setEnabling(true);
    try {
      // 1. Request permission
      let perm = Notification.permission;
      if (perm === "default") {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== "granted") {
        return { ok: false, error: "Permiso denegado por el navegador" };
      }

      // 2. Fetch the public VAPID key from backend
      const { data: keyRes } = await webApi.get("/web/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(keyRes.public_key);

      // 3. Ensure SW is ready, then subscribe
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      // 4. Send to backend
      const payload = sub.toJSON();
      await webApi.post("/web/push/subscribe", {
        endpoint: payload.endpoint,
        keys: payload.keys,
        user_agent: navigator.userAgent.slice(0, 280),
      });

      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      console.error("[push] enable failed", e);
      return { ok: false, error: e?.message || String(e) };
    } finally {
      setEnabling(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return { ok: false };
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const payload = sub.toJSON();
        await sub.unsubscribe();
        try {
          await webApi.post("/web/push/unsubscribe", {
            endpoint: payload.endpoint,
            keys: payload.keys || { p256dh: "x", auth: "x" },
          });
        } catch (unsubErr) {
          // Backend may already have GC'd the subscription — not fatal.
          console.debug("[usePushNotifications] backend unsubscribe failed:", unsubErr?.message);
        }
      }
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      console.error("[push] disable failed", e);
      return { ok: false, error: e?.message || String(e) };
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await webApi.post("/web/push/test");
      return data;
    } catch (e) {
      return { ok: false, error: e?.message };
    }
  }, []);

  return { supported, permission, subscribed, enabling, enable, disable, sendTest };
}
